import React, { useEffect, useState } from "react";
import {
    TextField, Button, Grid, Box, Card, Toolbar, Typography, Tooltip, IconButton, CardContent, Dialog
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel"
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../../Context/Contexts";
import CenteredCard from "../../../../components/Card/CenteredCardLayout";
import { ItemDimensionGroupViewModel, ItemDimensionViewModel, ListQuery } from "../../../../Types/ExportTypes";
import DefaultActions from "../../../../components/Card/DefaultActions";
import useCachedListQuery from "../../../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import ResponsivePagination from "../../../../components/ResponsivePagination";

const SizeGroupEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { code = "" } = params || {};
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const { showLoading, hideLoading } = useLoading();
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { goBack, navigate } = useNavigation();
    const [viewModel, setViewModel] = useState<ItemDimensionGroupViewModel | null>(null);
    const [query, setQuery] = useState<ListQuery>({ page: 0, pageSize: 100, filter: "", filters: [] });
    const [totalCount, setTotalCount] = useState(0);
    const [editingLine, setEditingLine] = useState<ItemDimensionViewModel | null>(null);
    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    useEffect(() => {
        if (code) {
            setIsLoading(true);
            authService
                .get<ItemDimensionGroupViewModel>(`/itemApi/GetSizeGroup?code=${code}`)
                .then(setViewModel)
                .catch((error: any) => {
                    showSnackbar(`${t("dialog.groupFetchError")} : ${error.response?.data ?? error.message}`, "error");
                })
                .finally(() => {
                    setIsLoading(false);
                } );
        } else {
            // Default empty location object for creating new entries
            setViewModel({
                createNew: true
            });
        }
    }, [code, authService]);


    const handleSave = async () => {
        if (!viewModel) return;
        if (!validateViewModel()) return; // Prevent save if validation fails
        var newItem = viewModel.createNew ?? false;

        setIsLoading(true);
        try {
            const response = await authService.post<ItemDimensionGroupViewModel>("/itemApi/CreateOrUpdateSizeGroup", viewModel);
            setViewModel(response);
            showSnackbar(t("dialog.groupSaveSuccess"), "success");

            if (newItem) {
                navigate({ path: "/purchase/item/sizegroup/edit", params: { code: response.code } });
            }
        } catch {
            showSnackbar(t("dialog.groupSaveError"), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (field: keyof ItemDimensionGroupViewModel, value: any) => {
        setViewModel((prev) => (prev ? { ...prev, [field]: value } : null));
    };


    const handleDelete = async () => {
    
        const isConfirmed = window.confirm(t("dialog.deleteRowConfirm"));
        if (!isConfirmed) return;
        var newItem = viewModel?.createNew ?? false;
        if (newItem) {
            navigate({ path: "/purchase/item/sizegroup/list" });
            return;
        }

        setIsLoading(true);
        try {
            await authService.post(`/itemApi/DeleteSizeGroup`, viewModel);
            showSnackbar(t("dialog.groupDeleteSuccess"), "success");

            navigate({ path: "/purchase/item/sizegroup/list" });
        } catch (error: any) {
            showSnackbar(`${t("dialog.groupDeleteError")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            setIsLoading(false);
        }
    };



    const validateViewModel = () => {
        if (!viewModel) {
            showSnackbar(t("dialog.dataMissing"), "error");
            return false;
        }


        if (!viewModel.description || viewModel.description.trim() === "") {
            showSnackbar(t("dialog.descriptionRequired"), "warning");
            return false;
        }


        return true;
    };


    const handleAddNewLine = async () => {
        setIsLoading(true);

        setEditingLine({
            createNew: true,
            groupCode : viewModel?.code
        });
        setIsLoading(false);
    };
    const handleEditRow = async (line: ItemDimensionViewModel) => {
        setIsLoading(true);
        setEditingLine(line);
        setIsLoading(false);
    };

    const handleFieldChange = async (field: keyof ItemDimensionViewModel, value: any) => {
        if (editingLine) {
            let updatedLine = { ...editingLine, [field]: value }
       

            setEditingLine((prev) => {
                if (!prev) return null;

            
                updatedLine = { ...prev, [field]: value };
                return updatedLine;
            });

        };
    }

    const handleSaveLine = async () => {

        if (!editingLine) return;
        if (!validateDimension(editingLine)) return;

        try {
            setIsLoading(true);
            const updatedLine = await authService.post<ItemDimensionViewModel>("itemApi/CreateOrUpdateSize", editingLine);

                // Update existing row in the list
            setViewModel((prev) => ({
                ...prev,
                dimensions: prev?.dimensions
                    ? prev.dimensions.some((line) => line.code === updatedLine.code)
                        ? prev.dimensions.map((line) => (line.code === updatedLine.code ? updatedLine : line))
                        : [...prev.dimensions, updatedLine]
                    : [updatedLine],
            }));

       
            showSnackbar(t("dialog.lineSaved"), "success");
            setEditingLine(null);
        } catch (error: any) {
            console.error("Error saving size:", error);
            showSnackbar(`${t("dialog.lineSaveError")} :  ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const validateDimension = (line: ItemDimensionViewModel) => {
        if (!line) {
            showSnackbar(t("dialog.lineDataMissing"), "error");
            return false;
        }


        if (!line.code || line.code.trim() === "") {
            showSnackbar(t("dialog.codeRequired"), "warning");
            return false;
        }
        if (!line.description || line.description.trim() === "") {
            showSnackbar("Description is required.", "warning");
            return false;
        }


        return true;
    };

    const handleRowDelete = async (salesLine: ItemDimensionViewModel) => {
        const isConfirmed = window.confirm(t("dialog.deleteRowConfirm"));
        if (!isConfirmed) return;
        try {
            await authService.post("itemApi/DeleteSize", salesLine);
            setViewModel((prev) => ({
                ...prev,
                dimensions: prev?.dimensions?.filter((line) =>
                    line.code !== salesLine.code 
                ) || [],
            }));
        } catch (error: any) {
            console.error("Failed to delete row:", error);
            showSnackbar(`${t("dialog.rowDeleteError")} :  ${error.response?.data ?? error.message}`, "error");
        }
        setQuery((prev) => ({ ...prev, page: 0, totalCount: totalCount - 1 }))
    };

    return (
        <>

            <CenteredCard title={t("common.editSizeGroup")} isLoading={isLoading} handleSave={handleSave}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    maxWidth: "fit-content",
                    mb: 2
                }}>
                    <DefaultActions
                        onSave={handleSave}
                        onDelete={handleDelete}
                    />
                </Box>
                {viewModel && (
                    <>
                        <Grid container spacing={2}>
                 


                                    <Grid item xs={12}>
                                        <TextField
                                          label={t("common.code")}
                                            value={viewModel.code || ""}

                                            fullWidth
                                            margin="normal"
                                            disabled

                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                             label={t("common.description")}
                                            value={viewModel.description || ""}

                                            fullWidth
                                    margin="normal"
                                    onChange={(e) => handleChange("description", e.target.value)}
                                    error={!viewModel.description || viewModel.description.trim() === ""}
                                    helperText={!viewModel.description || viewModel.description.trim() === "" ?  t("help.descriptionRequired")  : ""}
                                        />
                                    </Grid>
                           

                
                        </Grid>
                        {!viewModel.createNew && (
                            <>
                                <Box
                                    sx={{
                                        backgroundColor: "white",
                                        color: "white",
                                        borderRadius: "4px",
                                        marginBottom: "1rem",
                                        display: "flex",
                                        justifyContent: "center", // Center buttons horizontally
                                    }}
                                >

                                    <Grid container spacing={2} justifyContent="center" alignItems="center">
                                        {/* Add New Line */}
                                        <Grid item xs={12} sx={{ textAlign: "center" }}>
                                            <Button variant="contained" onClick={async () => handleAddNewLine()}
                                                startIcon={<AddIcon />}
                                                size="small"
                                            >
                                                {t("common.add")} {t("common.size")}
                                            </Button>
                                        </Grid>



                                    </Grid>

                                </Box>
                                <Box sx={{ padding: "1rem" }}>
                                    <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center" alignItems="center">
                                        {viewModel?.dimensions?.map((item) => (
                                            <Grid item xs={12} lg={6} key={item.code}>
                                                <Grid container spacing={1} sx={{
                                                    p: 1.5,
                                                    border: '1px solid #ccc',
                                                    borderRadius: 1,
                                                    cursor: "pointer",
                                                    transition: "all 0.2s ease-in-out",
                                                    ':hover': {
                                                        backgroundColor: '#f9f9f9'
                                                    }
                                                }}>
                                                    <Grid item xs={8}>
                                                        <Typography variant="subtitle1" >{item.code}</Typography>
                                                    </Grid>
                                                    <Grid item xs={2}>
                                                        <Tooltip title={t("common.edit")}>
                                                            <IconButton size="small" color="primary" onClick={() => handleEditRow(item)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item xs={2}>
                                                        <Tooltip title={t("common.delete")}>
                                                            <IconButton size="small" color="error" onClick={async () => await handleRowDelete(item)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <Typography variant="body1" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                                                            {item.description}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                        ))}
                                    </Grid>
                                </Box>
                            </>
                        )}
                    </>
                )}

                <ResponsivePagination
                    page={query.page}
                    pageSize={query.pageSize}
                    totalCount={totalCount}
                    onPageChange={(newPage) => setQuery((prev) => ({ ...prev, page: newPage }))}
                    sx={{ marginTop: "1rem" }}
          />

          <Box sx={{ marginTop: 2, textAlign: "center" }}>
            <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
              {t("common.save")}
            </Button>
            <Button variant="outlined" onClick={goBack}>
              {t("common.back")}
            </Button>
          </Box>
            </CenteredCard>

            <Dialog
                open={!!editingLine}
                onClose={() => setEditingLine(null)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: "8px", maxHeight: "80vh", overflowY: "auto" } }}
            >
                <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="h6">{t("common.edit")} {t("common.size")}</Typography>


                    {editingLine && (
                        <Grid container spacing={2}>

                            <Grid item xs={12} lg={12}>
                                <Box sx={{ backgroundColor: "secondary.main" }}>
                                    <TextField
                                        label={t("common.code")}
                                        variant="outlined"
                                        fullWidth
                                        value={editingLine?.code || ""}
                                        onChange={(e) => handleFieldChange("code", e.target.value)}
                                        error={!editingLine.code || editingLine.code.trim() === ""}
                                        helperText={!editingLine.code || editingLine.code.trim() === "" ?  t("help.codeRequired")  : ""}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} lg={12}>
                                <Box sx={{ backgroundColor: "secondary.main" }}>
                                    <TextField
                                        label={t("common.description")}
                                        variant="outlined"
                                        fullWidth
                                        value={editingLine?.description || ""}
                                        onChange={(e) => handleFieldChange("description", e.target.value)}
                                        error={!editingLine.description || editingLine.description.trim() === ""}
                                        helperText={!editingLine.description || editingLine.description.trim() === "" ?  t("´help.descriptionRequired")  : ""}
                                    />
                                </Box>
                            </Grid>
               
         

                        </Grid>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                        <Button onClick={handleSaveLine} variant="contained" startIcon={<SaveIcon />} disabled={!editingLine}>
                            {t("common.save")}
                        </Button>
                        <Button onClick={() => setEditingLine(null)} variant="outlined" startIcon={<CancelIcon />}>
                            {t("common.cancel")}
                        </Button>

                    </Box>
                </Box>
            </Dialog>

      
        </>
    );
};

export default SizeGroupEditPage;